import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Stethoscope, Activity, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOfflineData } from "@/contexts/OfflineDataContext";

export default function SymptomsPage() {
  const { symptoms, getSymptomProtocols } = useOfflineData();
  const [selectedSymptom, setSelectedSymptom] = useState(null);
  const [symptomProtocols, setSymptomProtocols] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSymptomClick = async (symptom) => {
    if (selectedSymptom?.id === symptom.id) {
      setSelectedSymptom(null);
      setSymptomProtocols([]);
      return;
    }
    setSelectedSymptom(symptom);
    try {
      const res = await getSymptomProtocols(symptom.id);
      setSymptomProtocols(res?.protocols || []);
    } catch {
      setSymptomProtocols([]);
    }
  };

  const filteredSymptoms = symptoms.filter(s =>
    !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="symptoms-page">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Stethoscope className="w-6 h-6" />
          Search by Symptom
        </h1>
        <p className="text-muted-foreground">Select a presenting symptom to find relevant protocols</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Filter symptoms..."
          className="pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="symptom-search-input"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredSymptoms.map(symptom => (
          <Card
            key={symptom.id}
            className={`cursor-pointer transition-all hover:border-primary/50 hover:shadow-md ${selectedSymptom?.id === symptom.id ? 'border-primary ring-2 ring-primary/20' : ''}`}
            onClick={() => handleSymptomClick(symptom)}
            data-testid={`symptom-card-${symptom.id}`}
          >
            <CardContent className="pt-5 pb-4">
              <h3 className="font-heading font-semibold text-base">{symptom.name}</h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{symptom.description}</p>
              <Badge variant="secondary" className="mt-2">{symptom.protocol_count} protocols</Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredSymptoms.length === 0 && (
        <Card className="p-8 text-center">
          <Stethoscope className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No symptoms match your search.</p>
        </Card>
      )}

      {selectedSymptom && symptomProtocols.length > 0 && (
        <Card className="border-primary" data-testid="symptom-protocols-panel">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="font-heading text-lg">{selectedSymptom.name}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{symptomProtocols.length} relevant protocols</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => { setSelectedSymptom(null); setSymptomProtocols([]); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {symptomProtocols.map(p => (
                <Link
                  key={p.id}
                  to={`/protocol/${p.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 transition-colors"
                  data-testid={`symptom-protocol-link-${p.id}`}
                >
                  <Activity className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm">{p.name}</span>
                  </div>
                  <Badge variant="outline" className="text-xs shrink-0">{p.category}</Badge>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
